/**
 * Pure visibility helper for the CafeteriaLauncher.
 *
 * Extracted from `launcher.tsx` so it can be unit-tested without pulling
 * in React / `@ordo/ui` / the zustand stores. Type-only import of
 * `Profile` (erased at runtime), so this module has zero runtime deps.
 *
 * Cafeteria Mode is launcher-only (the numbered nav item was removed),
 * so this predicate is the single gate for reaching `/cafeteria` from
 * the sidebar. It returns true when ANY of:
 *
 *   - the tab is a kiosk-paired tablet (`kioskContext.paired`),
 *   - the server says so: `profile.cafeteriaAccess.enabled`, the verdict of
 *     `server/src/utils/cafeteria-auth.ts` `hasCafeteriaAccess` — school
 *     staff by profile OR a chef holding an effective cafeteria grant issued
 *     by the Ordo team in Customers (PROD-1168). This is the ONLY way a
 *     chef-side user reaches the launcher: `profileCodes` cannot see grants,
 *     so the client must not try to re-derive it,
 *   - (deploy-skew fallback) the user has a CAFETERIA_WORKER membership,
 *   - (fallback) a SCHOOL_ADMIN membership — the server already authorizes
 *     SCHOOL_ADMIN for /api/cafeteria/* and hands them their campuses from
 *     `locationsForWorker`, so the school admin who runs service is allowed
 *     to serve and must have an entry point, or
 *   - (fallback) an Ordo staff session, `profile.isOrdoStaff` (panopticon / walk-through
 *     debugging — the access the removed nav item used to grant them).
 *
 * The three profile-code branches duplicate what `cafeteriaAccess.enabled`
 * already says for school staff; they stay so an older server that omits
 * the field (the client store defaults it to disabled) never hides the
 * launcher from a real cafeteria worker.
 *
 * Impersonation deliberately gets NO branch of its own. An Ordo-takeover
 * session swaps the whole identity, so the payload already describes the
 * impersonated user: "Access School View" surfaces SCHOOL_ADMIN and
 * qualifies through the plain branch above, while a chef-view takeover
 * surfaces CHEF_ADMIN and qualifies through `cafeteriaAccess.enabled`: the
 * org's synthetic view identity inherits every effective grant the org's
 * chefs hold (`@ordo/schema` `resolveChefCafeteriaGrants`), so Ops sees in
 * the take-over what they granted. Gating SCHOOL_ADMIN on impersonation — as
 * this helper used to — hid the launcher from every real school admin while
 * revealing it to operators pretending to be one (PROD-331).
 */

import type { Profile } from '../../stores/profile'

export function shouldShowCafeteriaLauncher(profile: Profile | null): boolean {
  if (!profile) {
    return false
  }
  return (
    profile.kioskContext.paired ||
    profile.cafeteriaAccess.enabled ||
    profile.profileCodes.includes('CAFETERIA_WORKER') ||
    profile.profileCodes.includes('SCHOOL_ADMIN') ||
    profile.isOrdoStaff
  )
}
